import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Package,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  Save,
  ArrowLeft,
  Hotel,
  X
} from 'lucide-react'

interface PackageData {
  id: string
  name: string
  package_type: string
  duration_days: number
  max_participants: number
  base_price: number
  currency: string
  description: string
  location?: string
  city?: string
  country?: string
  region?: string
  accommodation_count: number
  activity_count: number
  transfer_count: number
  components?: {
    accommodations: string[]
    activities: string[]
    transfers: string[]
  }
  is_active: boolean
  created_at: string
}

interface PackageManagementProps {
  tenantId: string
}

const PackageManagement: React.FC<PackageManagementProps> = ({ tenantId }) => {
  return (
    <Routes>
      <Route path="/" element={<PackageList tenantId={tenantId} />} />
      <Route path="/new" element={<PackageForm tenantId={tenantId} />} />
      <Route path="/edit/:id" element={<PackageForm tenantId={tenantId} />} />
      <Route path="/view/:id" element={<PackageView tenantId={tenantId} />} />
    </Routes>
  )
}

// Package List Component
const PackageList: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [packages, setPackages] = useState<PackageData[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')

  const packageTypes = ['romantic', 'adventure', 'family', 'luxury', 'budget', 'cultural']

  useEffect(() => {
    fetchPackages()
  }, [tenantId])

  const fetchPackages = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/packages?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        setPackages(data.packages)
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error)
    } finally {
      setLoading(false)
    }
  }

  const deletePackage = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/packages/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        setPackages(packages.filter(pkg => pkg.id !== id))
      }
    } catch (error) {
      console.error('Failed to delete package:', error)
    }
  }

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || pkg.package_type === filterType
    const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' ? pkg.is_active : !pkg.is_active)
    return matchesSearch && matchesType && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Package Management</h1>
          <p className="text-gray-600">Create and manage travel packages</p>
        </div>
        <Link
          to="/dashboard/packages/new"
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-150"
        >
          <Plus className="w-4 h-4" />
          Create Package
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search packages by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              {packageTypes.map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPackages.map((pkg) => (
          <div key={pkg.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Package Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 text-white relative">
              <div className="absolute top-2 right-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  pkg.is_active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {pkg.is_active ? 'active' : 'inactive'}
                </span>
              </div>
              <h3 className="text-lg font-semibold truncate pr-16">{pkg.name}</h3>
              <div className="flex items-center text-purple-100 text-sm mt-1">
                <Package className="w-4 h-4 mr-1" />
                {pkg.package_type.charAt(0).toUpperCase() + pkg.package_type.slice(1)}
              </div>
            </div>

            {/* Package Info */}
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {pkg.duration_days} days
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Max {pkg.max_participants}
                </div>
                {pkg.location && (
                  <div className="flex items-center col-span-2">
                    <MapPin className="w-4 h-4 mr-1" />
                    {pkg.location}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="text-lg font-semibold text-purple-600">
                  ${pkg.base_price} {pkg.currency || 'USD'}
                </div>
              </div>

              <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                {pkg.description}
              </p>

              {/* Component Count */}
              <div className="flex justify-between text-xs text-gray-500 mb-4">
                <span>{pkg.accommodation_count || 0} Hotels</span>
                <span>{pkg.activity_count || 0} Activities</span>
                <span>{pkg.transfer_count || 0} Transfers</span>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <Link
                  to={`/dashboard/packages/view/${pkg.id}`}
                  className="text-purple-600 hover:text-purple-700 p-1 rounded"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                <Link
                  to={`/dashboard/packages/edit/${pkg.id}`}
                  className="text-blue-600 hover:text-blue-700 p-1 rounded"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => deletePackage(pkg.id)}
                  className="text-red-600 hover:text-red-700 p-1 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPackages.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No packages found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria.' 
              : 'Get started by creating your first package.'}
          </p>
          {!searchTerm && filterType === 'all' && filterStatus === 'all' && (
            <div className="mt-6">
              <Link
                to="/dashboard/packages/new"
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Package
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Package Form Component
const PackageForm: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const { id } = useParams() // Get package ID from URL for editing
  const isEditing = Boolean(id)
  const [loading, setLoading] = useState(false)
  const [loadingPackage, setLoadingPackage] = useState(isEditing)
  const [availableHotels, setAvailableHotels] = useState<any[]>([])
  const [availableActivities, setAvailableActivities] = useState<any[]>([])
  
  const [packageData, setPackageData] = useState({
    name: '',
    package_type: '',
    duration_days: 3,
    max_participants: 10,
    base_price: 0,
    currency: 'USD',
    description: '',
    location: '',
    city: '',
    country: '',
    region: '',
    components: {
      accommodations: [] as string[],
      activities: [] as string[],
      transfers: [] as string[]
    },
    itinerary: [] as Array<{day: number, title: string, description: string, activities: string[]}>,
    images: [] as string[],
    status: 'active' as 'active' | 'inactive',
    pricing_type: 'fixed' as 'fixed' | 'component_based' | 'seasonal',
    includes: [] as string[],
    excludes: [] as string[]
  })

  const [pricingConfig, setPricingConfig] = useState({
    // Component-based pricing
    markup_type: 'percentage' as 'percentage' | 'amount',
    markup_value: 10,
    component_markup: {
      accommodations: { type: 'percentage', value: 15 },
      activities: { type: 'percentage', value: 20 },
      transfers: { type: 'amount', value: 50 }
    },
    // Seasonal pricing
    seasons: [] as Array<{
      id: string,
      name: string,
      description: string,
      start_date: string,
      end_date: string,
      rates: {
        '2_pax_shared': { price_per_person: number },
        '4_pax_shared': { price_per_person: number },
        '6_pax_shared': { price_per_person: number },
        'solo_traveler': { price_per_person: number },
        'single_supplement': { amount: number }
      }
    }>
  })

  const packageTypes = ['romantic', 'adventure', 'family', 'luxury', 'budget', 'cultural']

  useEffect(() => {
    fetchAvailableComponents()
    if (isEditing && id) {
      fetchPackageForEdit()
    }
  }, [tenantId, id, isEditing])

  const fetchPackageForEdit = async () => {
    try {
      setLoadingPackage(true)
      const response = await authFetch(`${API_BASE_URL}/api/packages/${id}?tenant_id=${tenantId}`)
      const data = await response.json()

      if (data.success && data.package) {
        const pkg = data.package
        setPackageData({
          name: pkg.name || '',
          package_type: pkg.package_type || '',
          duration_days: pkg.duration_days || 3,
          max_participants: pkg.max_participants || 10,
          base_price: pkg.base_price || 0,
          currency: pkg.currency || 'USD',
          description: pkg.description || '',
          location: pkg.location || '',
          city: pkg.city || '',
          country: pkg.country || '',
          region: pkg.region || '',
          components: {
            accommodations: pkg.components?.accommodations?.map((acc: any) => acc.id || acc) || [],
            activities: pkg.components?.activities?.map((act: any) => act.id || act) || [],
            transfers: pkg.components?.transfers?.map((trans: any) => trans.id || trans) || []
          },
          itinerary: pkg.itinerary || [],
          images: pkg.images || [],
          status: pkg.is_active ? 'active' : 'inactive',
          pricing_type: pkg.pricing_type || 'fixed',
          includes: Array.isArray(pkg.includes) ? pkg.includes : [],
          excludes: Array.isArray(pkg.excludes) ? pkg.excludes : []
        })
      } else {
        alert('Failed to load package for editing')
        navigate('/dashboard/packages')
      }
    } catch (error) {
      console.error('Failed to fetch package for editing:', error)
      alert('Failed to load package for editing')
      navigate('/dashboard/packages')
    } finally {
      setLoadingPackage(false)
    }
  }

  const fetchAvailableComponents = async () => {
    try {
      const [hotelsRes, activitiesRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/admin/accommodations?tenant_id=${tenantId}`),
        authFetch(`${API_BASE_URL}/api/admin/activities?tenant_id=${tenantId}`)
      ])

      const hotelsData = await hotelsRes.json()
      const activitiesData = await activitiesRes.json()

      if (hotelsData.success) setAvailableHotels(hotelsData.accommodations)
      if (activitiesData.success) setAvailableActivities(activitiesData.activities)
    } catch (error) {
      console.error('Failed to fetch components:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Validate field lengths to prevent database errors
      if (packageData.name.length > 255) {
        alert('Package name must be 255 characters or less')
        setLoading(false)
        return
      }
      
      if (packageData.package_type.length > 100) {
        alert('Package type must be 100 characters or less')
        setLoading(false)
        return
      }

      // Validate and sanitize numeric fields to prevent NaN
      const sanitizedData = {
        ...packageData,
        duration_days: isNaN(packageData.duration_days) ? 1 : packageData.duration_days,
        max_participants: isNaN(packageData.max_participants) ? 1 : packageData.max_participants,
        base_price: isNaN(packageData.base_price) ? 0 : packageData.base_price,
        currency: packageData.currency || 'USD',
        itinerary: packageData.itinerary || [],
        images: packageData.images || [],
        includes: (packageData.includes || []).filter(item => item.trim() !== ''),
        excludes: (packageData.excludes || []).filter(item => item.trim() !== '')
      }
      
      const url = isEditing 
        ? `${API_BASE_URL}/api/admin/packages/${id}?tenant_id=${tenantId}`
        : `${API_BASE_URL}/api/admin/packages?tenant_id=${tenantId}`
      
      const method = isEditing ? 'PUT' : 'POST'
      
      const response = await authFetch(url, {
        method: method,
        body: JSON.stringify(sanitizedData)
      })

      if (response.ok) {
        navigate('/dashboard/packages')
      } else {
        const errorData = await response.text()
        console.error(`Failed to ${isEditing ? 'update' : 'create'} package:`, response.status, errorData)
        alert(`Failed to ${isEditing ? 'update' : 'create'} package: ${response.status} - ${errorData}`)
      }
    } catch (error) {
      console.error('Error saving package:', error)
      alert('Error saving package. Please check the console for details.')
    } finally {
      setLoading(false)
    }
  }

  const toggleComponent = (type: 'accommodations' | 'activities' | 'transfers', componentId: string) => {
    setPackageData(prev => ({
      ...prev,
      components: {
        ...prev.components,
        [type]: prev.components[type].includes(componentId)
          ? prev.components[type].filter(id => id !== componentId)
          : [...prev.components[type], componentId]
      }
    }))
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData()
        formData.append('file', file)

        const response = await authFetch(`${API_BASE_URL}/api/admin/packages/upload-image`, {
          method: 'POST',
          body: formData
        })

        if (response.ok) {
          const data = await response.json()
          return data.path // Return the relative path for database storage
        } else {
          throw new Error('Failed to upload image')
        }
      })

      const uploadedPaths = await Promise.all(uploadPromises)
      
      setPackageData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedPaths]
      }))

    } catch (error) {
      console.error('Failed to upload images:', error)
      alert('Failed to upload one or more images. Please try again.')
    }

    // Reset the input
    e.target.value = ''
  }

  if (loadingPackage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/packages')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Package' : 'Create New Package'}
          </h1>
          <p className="text-gray-600">
            {isEditing ? 'Update your travel package' : 'Build a comprehensive travel package'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name *
              </label>
              <input
                type="text"
                required
                maxLength={255}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={packageData.name}
                onChange={(e) => setPackageData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Paris Romance Package"
              />
              <div className="text-xs text-gray-500 mt-1">
                {packageData.name.length}/255 characters
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Type *
              </label>
              <select
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={packageData.package_type}
                onChange={(e) => setPackageData(prev => ({ ...prev, package_type: e.target.value }))}
              >
                <option value="">Select Type</option>
                {packageTypes.map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (days) *
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={packageData.duration_days}
                onChange={(e) => setPackageData(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 1 }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Participants *
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                value={packageData.max_participants}
                onChange={(e) => setPackageData(prev => ({ ...prev, max_participants: parseInt(e.target.value) || 1 }))}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Price *
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.base_price}
                  onChange={(e) => setPackageData(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
                <select
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.currency}
                  onChange={(e) => setPackageData(prev => ({ ...prev, currency: e.target.value }))}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                  <option value="JPY">JPY</option>
                  <option value="CHF">CHF</option>
                  <option value="CNY">CNY</option>
                  <option value="INR">INR</option>
                  <option value="SGD">SGD</option>
                  <option value="KES">KES</option>
                  <option value="ZAR">ZAR</option>
                </select>
              </div>
            </div>

          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={packageData.description}
              onChange={(e) => setPackageData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the package, what makes it special, target audience..."
            />
          </div>

          {/* Inclusions & Exclusions */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">What's Included & Not Included</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* What's Included */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ✅ What's Included
                </label>
                <div className="space-y-2">
                  {packageData.includes.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        value={item}
                        onChange={(e) => {
                          const newIncludes = [...packageData.includes]
                          newIncludes[index] = e.target.value
                          setPackageData(prev => ({ ...prev, includes: newIncludes }))
                        }}
                        placeholder="e.g., Daily breakfast"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newIncludes = packageData.includes.filter((_, i) => i !== index)
                          setPackageData(prev => ({ ...prev, includes: newIncludes }))
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPackageData(prev => ({ ...prev, includes: [...prev.includes, ''] }))}
                    className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors"
                  >
                    + Add Inclusion
                  </button>
                </div>
              </div>

              {/* What's Not Included */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ❌ What's Not Included
                </label>
                <div className="space-y-2">
                  {packageData.excludes.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        value={item}
                        onChange={(e) => {
                          const newExcludes = [...packageData.excludes]
                          newExcludes[index] = e.target.value
                          setPackageData(prev => ({ ...prev, excludes: newExcludes }))
                        }}
                        placeholder="e.g., International flights"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newExcludes = packageData.excludes.filter((_, i) => i !== index)
                          setPackageData(prev => ({ ...prev, excludes: newExcludes }))
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPackageData(prev => ({ ...prev, excludes: [...prev.excludes, ''] }))}
                    className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-red-500 hover:text-red-600 transition-colors"
                  >
                    + Add Exclusion
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Location Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location/Description
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.location}
                  onChange={(e) => setPackageData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Nairobi, Kenya or Masai Mara National Reserve"
                />
                <div className="text-xs text-gray-500 mt-1">
                  General location description for display
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.city}
                  onChange={(e) => setPackageData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="e.g., Nairobi, Arusha, London"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.country}
                  onChange={(e) => setPackageData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="e.g., Kenya, Tanzania, United Kingdom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={packageData.region}
                  onChange={(e) => setPackageData(prev => ({ ...prev, region: e.target.value }))}
                  placeholder="e.g., East Africa, Europe, North America"
                />
              </div>
            </div>
          </div>

          {/* Pricing Configuration */}
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Configuration</h3>
            
            {/* Pricing Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Pricing Type {isEditing && <span className="text-orange-600 text-xs">(Cannot be changed after creation)</span>}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  packageData.pricing_type === 'fixed' 
                    ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-600' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="pricing-type"
                    value="fixed"
                    checked={packageData.pricing_type === 'fixed'}
                    onChange={(e) => !isEditing && setPackageData(prev => ({ ...prev, pricing_type: e.target.value as any }))}
                    className="sr-only"
                    disabled={isEditing}
                  />
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">Fixed Price</div>
                        <div className="text-gray-500">Single price for all dates</div>
                      </div>
                    </div>
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  packageData.pricing_type === 'component_based' 
                    ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-600' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="pricing-type"
                    value="component_based"
                    checked={packageData.pricing_type === 'component_based'}
                    onChange={(e) => !isEditing && setPackageData(prev => ({ ...prev, pricing_type: e.target.value as any }))}
                    className="sr-only"
                    disabled={isEditing}
                  />
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">Component-Based</div>
                        <div className="text-gray-500">Sum of components + markup</div>
                      </div>
                    </div>
                    <Package className="h-5 w-5 text-purple-600" />
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                  packageData.pricing_type === 'seasonal' 
                    ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-600' 
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                } ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="radio"
                    name="pricing-type"
                    value="seasonal"
                    checked={packageData.pricing_type === 'seasonal'}
                    onChange={(e) => !isEditing && setPackageData(prev => ({ ...prev, pricing_type: e.target.value as any }))}
                    className="sr-only"
                    disabled={isEditing}
                  />
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">Seasonal Pricing</div>
                        <div className="text-gray-500">Date-based pricing variations</div>
                      </div>
                    </div>
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed Pricing Configuration */}
            {packageData.pricing_type === 'fixed' && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Fixed Pricing</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Package will use the base price set above for all bookings.
                </p>
                <div className="text-lg font-semibold text-purple-600">
                  ${packageData.base_price} {packageData.currency}
                </div>
              </div>
            )}

            {/* Component-Based Pricing Configuration */}
            {packageData.pricing_type === 'component_based' && (
              <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-4">
                <h4 className="font-medium text-gray-900">Component-Based Pricing</h4>
                
                {/* Global Markup */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Global Markup Type
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={pricingConfig.markup_type}
                      onChange={(e) => setPricingConfig(prev => ({ ...prev, markup_type: e.target.value as any }))}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="amount">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Markup Value
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={pricingConfig.markup_type === 'percentage' ? 200 : 10000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={pricingConfig.markup_value}
                      onChange={(e) => setPricingConfig(prev => ({ ...prev, markup_value: parseFloat(e.target.value) || 0 }))}
                      placeholder={pricingConfig.markup_type === 'percentage' ? '15' : '100'}
                    />
                  </div>
                </div>

                {/* Component-Specific Markup */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Component-Specific Markup</h5>
                  <div className="space-y-3">
                    {/* Accommodations */}
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <Hotel className="w-5 h-5 text-purple-600" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">Accommodations</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.accommodations.type}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              accommodations: { ...prev.component_markup.accommodations, type: e.target.value }
                            }
                          }))}
                        >
                          <option value="percentage">%</option>
                          <option value="amount">$</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.accommodations.value}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              accommodations: { ...prev.component_markup.accommodations, value: parseFloat(e.target.value) || 0 }
                            }
                          }))}
                        />
                      </div>
                    </div>

                    {/* Activities */}
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-purple-600" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">Activities</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.activities.type}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              activities: { ...prev.component_markup.activities, type: e.target.value }
                            }
                          }))}
                        >
                          <option value="percentage">%</option>
                          <option value="amount">$</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.activities.value}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              activities: { ...prev.component_markup.activities, value: parseFloat(e.target.value) || 0 }
                            }
                          }))}
                        />
                      </div>
                    </div>

                    {/* Transfers */}
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="w-5 h-5 text-purple-600">🚐</div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">Transfers</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          className="px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.transfers.type}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              transfers: { ...prev.component_markup.transfers, type: e.target.value }
                            }
                          }))}
                        >
                          <option value="percentage">%</option>
                          <option value="amount">$</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                          value={pricingConfig.component_markup.transfers.value}
                          onChange={(e) => setPricingConfig(prev => ({
                            ...prev,
                            component_markup: {
                              ...prev.component_markup,
                              transfers: { ...prev.component_markup.transfers, value: parseFloat(e.target.value) || 0 }
                            }
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Seasonal Pricing Configuration */}
            {packageData.pricing_type === 'seasonal' && (
              <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900">Seasonal Pricing</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newSeason = {
                        id: `season-${Date.now()}`,
                        name: `Season ${pricingConfig.seasons.length + 1}`,
                        description: '',
                        start_date: '',
                        end_date: '',
                        rates: {
                          '2_pax_shared': { price_per_person: 0 },
                          '4_pax_shared': { price_per_person: 0 },
                          '6_pax_shared': { price_per_person: 0 },
                          'solo_traveler': { price_per_person: 0 },
                          'single_supplement': { amount: 0 }
                        }
                      }
                      setPricingConfig(prev => ({
                        ...prev,
                        seasons: [...prev.seasons, newSeason]
                      }))
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Season
                  </button>
                </div>

                {pricingConfig.seasons.map((season, seasonIndex) => (
                  <div key={season.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <h5 className="font-medium text-gray-900">Season {seasonIndex + 1}</h5>
                      <button
                        type="button"
                        onClick={() => {
                          setPricingConfig(prev => ({
                            ...prev,
                            seasons: prev.seasons.filter((_, i) => i !== seasonIndex)
                          }))
                        }}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Season Name</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          value={season.name}
                          onChange={(e) => {
                            const newSeasons = [...pricingConfig.seasons]
                            newSeasons[seasonIndex] = { ...season, name: e.target.value }
                            setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                          }}
                          placeholder="e.g., Spring Special Offer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          value={season.description}
                          onChange={(e) => {
                            const newSeasons = [...pricingConfig.seasons]
                            newSeasons[seasonIndex] = { ...season, description: e.target.value }
                            setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                          }}
                          placeholder="Peak season rates"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          value={season.start_date}
                          onChange={(e) => {
                            const newSeasons = [...pricingConfig.seasons]
                            newSeasons[seasonIndex] = { ...season, start_date: e.target.value }
                            setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          value={season.end_date}
                          onChange={(e) => {
                            const newSeasons = [...pricingConfig.seasons]
                            newSeasons[seasonIndex] = { ...season, end_date: e.target.value }
                            setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                          }}
                        />
                      </div>
                    </div>

                    {/* Pricing Grid */}
                    <div>
                      <h6 className="text-sm font-medium text-gray-700 mb-2">Pricing Rates (Per Person)</h6>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Group Size</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price per Person</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">2 Pax (Shared)</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                  value={season.rates['2_pax_shared'].price_per_person}
                                  onChange={(e) => {
                                    const newSeasons = [...pricingConfig.seasons]
                                    newSeasons[seasonIndex] = {
                                      ...season,
                                      rates: {
                                        ...season.rates,
                                        '2_pax_shared': { price_per_person: parseFloat(e.target.value) || 0 }
                                      }
                                    }
                                    setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">4 Pax (Shared)</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                  value={season.rates['4_pax_shared'].price_per_person}
                                  onChange={(e) => {
                                    const newSeasons = [...pricingConfig.seasons]
                                    newSeasons[seasonIndex] = {
                                      ...season,
                                      rates: {
                                        ...season.rates,
                                        '4_pax_shared': { price_per_person: parseFloat(e.target.value) || 0 }
                                      }
                                    }
                                    setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">6 Pax (Shared)</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                  value={season.rates['6_pax_shared'].price_per_person}
                                  onChange={(e) => {
                                    const newSeasons = [...pricingConfig.seasons]
                                    newSeasons[seasonIndex] = {
                                      ...season,
                                      rates: {
                                        ...season.rates,
                                        '6_pax_shared': { price_per_person: parseFloat(e.target.value) || 0 }
                                      }
                                    }
                                    setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Solo Traveler</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                  value={season.rates.solo_traveler.price_per_person}
                                  onChange={(e) => {
                                    const newSeasons = [...pricingConfig.seasons]
                                    newSeasons[seasonIndex] = {
                                      ...season,
                                      rates: {
                                        ...season.rates,
                                        'solo_traveler': { price_per_person: parseFloat(e.target.value) || 0 }
                                      }
                                    }
                                    setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                                  }}
                                />
                              </td>
                            </tr>
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Single Room Supplement</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                  value={season.rates.single_supplement.amount}
                                  onChange={(e) => {
                                    const newSeasons = [...pricingConfig.seasons]
                                    newSeasons[seasonIndex] = {
                                      ...season,
                                      rates: {
                                        ...season.rates,
                                        'single_supplement': { amount: parseFloat(e.target.value) || 0 }
                                      }
                                    }
                                    setPricingConfig(prev => ({ ...prev, seasons: newSeasons }))
                                  }}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}

                {pricingConfig.seasons.length === 0 && (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p>No seasons added yet. Click "Add Season" to create date-based pricing.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Package Components */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Package Components</h3>
            
            {/* Hotels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Hotels ({packageData.components.accommodations.length} selected)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {availableHotels.map(hotel => (
                  <label key={hotel.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={packageData.components.accommodations.includes(hotel.id)}
                      onChange={() => toggleComponent('accommodations', hotel.id)}
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">{hotel.name}</div>
                      <div className="text-sm text-gray-500">{hotel.city}, {hotel.country}</div>
                      <div className="text-sm text-purple-600">${hotel.base_price}/night</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Activities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Activities ({packageData.components.activities.length} selected)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {availableActivities.map(activity => (
                  <label key={activity.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={packageData.components.activities.includes(activity.id)}
                      onChange={() => toggleComponent('activities', activity.id)}
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">{activity.name}</div>
                      <div className="text-sm text-gray-500">{activity.location}</div>
                      <div className="text-sm text-purple-600">${activity.price}/person</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Day-wise Itinerary */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Day-wise Itinerary</h3>
              <button
                type="button"
                onClick={() => {
                  const newDay = packageData.itinerary.length + 1
                  setPackageData(prev => ({
                    ...prev,
                    itinerary: [...prev.itinerary, {
                      day: newDay,
                      title: `Day ${newDay}`,
                      description: '',
                      activities: []
                    }]
                  }))
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Day
              </button>
            </div>

            {packageData.itinerary.map((day, dayIndex) => (
              <div key={dayIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium text-gray-900">Day {day.day}</h4>
                  <button
                    type="button"
                    onClick={() => {
                      setPackageData(prev => ({
                        ...prev,
                        itinerary: prev.itinerary.filter((_, i) => i !== dayIndex)
                      }))
                    }}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day Title
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={day.title}
                      onChange={(e) => {
                        const newItinerary = [...packageData.itinerary]
                        newItinerary[dayIndex] = { ...day, title: e.target.value }
                        setPackageData(prev => ({ ...prev, itinerary: newItinerary }))
                      }}
                      placeholder="e.g., Arrival & City Tour"
                    />
                  </div>
                  
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={day.description}
                      onChange={(e) => {
                        const newItinerary = [...packageData.itinerary]
                        newItinerary[dayIndex] = { ...day, description: e.target.value }
                        setPackageData(prev => ({ ...prev, itinerary: newItinerary }))
                      }}
                      placeholder="Describe the day's activities and schedule..."
                    />
                  </div>
                </div>
              </div>
            ))}

            {packageData.itinerary.length === 0 && (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p>No itinerary added yet. Click "Add Day" to start building your day-wise plan.</p>
              </div>
            )}
          </div>

          {/* Package Images */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Package Images</h3>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Add Images
                </label>
              </div>
            </div>

            {packageData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {packageData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={typeof image === 'string' ? `${API_BASE_URL}/uploads/${image}` : image}
                      alt={`Package image ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiAxNkM5Ljc5IDMuNSA5Ljc5IDMuNSA5Ljc5IDMuNVM5Ljc5IDMuNSAxMiAxNloiIGZpbGw9IiM5Q0E0QUYiLz4KPC9zdmc+'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPackageData(prev => ({
                          ...prev,
                          images: prev.images.filter((_, i) => i !== index)
                        }))
                      }}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {packageData.images.length === 0 && (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="mx-auto h-8 w-8 text-gray-400 mb-2">📷</div>
                <p>No images added yet. Click "Add Images" to upload package photos.</p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard/packages')}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isEditing ? 'Update Package' : 'Create Package'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Package View Component
const PackageView: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [packageData, setPackageData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      fetchPackageDetails()
    }
  }, [id, tenantId])

  const fetchPackageDetails = async () => {
    try {
      setLoading(true)
      const response = await authFetch(`${API_BASE_URL}/api/packages/${id}?tenant_id=${tenantId}`)
      const data = await response.json()

      if (data.success) {
        setPackageData(data.package)
      } else {
        setError('Failed to load package details')
      }
    } catch (error) {
      console.error('Failed to fetch package details:', error)
      setError('Failed to load package details')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  if (error || !packageData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-lg">{error || 'Package not found'}</div>
        <button
          onClick={() => navigate('/dashboard/packages')}
          className="mt-4 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
        >
          Back to Packages
        </button>
      </div>
    )
  }

  const totalPrice = packageData.base_price || 0
  const componentsTotal = 
    (packageData.components?.accommodations?.reduce((sum: number, acc: any) => sum + (acc.price || 0), 0) || 0) +
    (packageData.components?.activities?.reduce((sum: number, acc: any) => sum + (acc.price || 0), 0) || 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/packages')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{packageData.name}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
            <span className="flex items-center">
              <Package className="w-4 h-4 mr-1" />
              {packageData.package_type?.charAt(0).toUpperCase() + packageData.package_type?.slice(1)}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {packageData.duration_days} days
            </span>
            <span className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              Max {packageData.max_participants} people
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              packageData.is_active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {packageData.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/dashboard/packages/edit/${id}`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit Package
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {packageData.description && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
              <div className="text-gray-700 whitespace-pre-wrap">
                {packageData.description}
              </div>
            </div>
          )}

          {/* Package Components */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Package Components</h2>
            
            {/* Accommodations */}
            {packageData.components?.accommodations?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <Hotel className="w-4 h-4 mr-2" />
                  Accommodations ({packageData.components.accommodations.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packageData.components.accommodations.map((acc: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-900">{acc.name}</div>
                      <div className="text-sm text-purple-600">${acc.price}/night</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activities */}
            {packageData.components?.activities?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  Activities ({packageData.components.activities.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packageData.components.activities.map((activity: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-900">{activity.name}</div>
                      <div className="text-sm text-purple-600">${activity.price}/person</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transfers */}
            {packageData.components?.transfers?.length > 0 && (
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">
                  Transfers ({packageData.components.transfers.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packageData.components.transfers.map((transfer: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-900">{transfer.name}</div>
                      <div className="text-sm text-purple-600">${transfer.price}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!packageData.components?.accommodations?.length && 
              !packageData.components?.activities?.length && 
              !packageData.components?.transfers?.length) && (
              <div className="text-center py-8 text-gray-500">
                No components added to this package yet.
              </div>
            )}
          </div>

          {/* Day-wise Itinerary */}
          {packageData.itinerary?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Day-wise Itinerary</h2>
              
              <div className="space-y-4">
                {packageData.itinerary.map((day: any, index: number) => (
                  <div key={index} className="border-l-4 border-purple-500 pl-4 py-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg">
                          Day {day.day}: {day.title}
                        </h3>
                        <p className="text-gray-700 mt-1 leading-relaxed">
                          {day.description}
                        </p>
                        {day.activities?.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm font-medium text-gray-600">Activities: </span>
                            <span className="text-sm text-gray-600">
                              {day.activities.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Includes & Excludes */}
          {(packageData.includes?.length > 0 || packageData.excludes?.length > 0) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h2>
              
              {packageData.includes?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-medium text-green-700 mb-2">Included</h3>
                  <ul className="space-y-1">
                    {packageData.includes.map((item: string, index: number) => (
                      <li key={index} className="flex items-start text-sm text-gray-700">
                        <span className="text-green-500 mr-2 mt-0.5">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {packageData.excludes?.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-red-700 mb-2">Not Included</h3>
                  <ul className="space-y-1">
                    {packageData.excludes.map((item: string, index: number) => (
                      <li key={index} className="flex items-start text-sm text-gray-700">
                        <span className="text-red-500 mr-2 mt-0.5">✗</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Terms & Conditions */}
          {packageData.terms_conditions && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Terms & Conditions</h2>
              <div className="text-gray-700 text-sm whitespace-pre-wrap">
                {packageData.terms_conditions}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Price</span>
                <span className="font-medium">${totalPrice}</span>
              </div>
              
              {componentsTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Components Total</span>
                  <span className="text-gray-500">${componentsTotal}</span>
                </div>
              )}
              
              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total Price</span>
                  <span className="text-purple-600">${totalPrice}</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Per person • {packageData.currency || 'USD'}
                </div>
              </div>
            </div>
          </div>

          {/* Package Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Package Information</h2>
            
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-500">Created</div>
                <div className="font-medium">
                  {new Date(packageData.created_at).toLocaleDateString()}
                </div>
              </div>
              
              {packageData.updated_at && (
                <div>
                  <div className="text-sm text-gray-500">Last Updated</div>
                  <div className="font-medium">
                    {new Date(packageData.updated_at).toLocaleDateString()}
                  </div>
                </div>
              )}
              
              <div>
                <div className="text-sm text-gray-500">Package ID</div>
                <div className="font-mono text-xs text-gray-700 break-all">
                  {packageData.id}
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          {packageData.images?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Images</h2>
              <div className="grid grid-cols-2 gap-2">
                {packageData.images.map((image: string, index: number) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={`${API_BASE_URL}/uploads/${image}`}
                      alt={`Package image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiAxNkM5Ljc5IDMuNSA5Ljc5IDMuNSA5Ljc5IDMuNVM5Ljc5IDMuNSAxMiAxNloiIGZpbGw9IiM5Q0E0QUYiLz4KPC9zdmc+'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PackageManagement